// src/utils/maintenance.ts

import { maintenanceConfig, IMaintenanceConfig } from 'src/maintenance.config';

export type MaintenanceState = 'normal' | 'scheduled' | 'active' | 'incident' | 'resolved';

export const getMaintenanceConfig = (): IMaintenanceConfig => {
  return maintenanceConfig;
};

export const getMaintenanceState = (): MaintenanceState => {
  const config = getMaintenanceConfig();

  if (!config.enabled) {
    return 'normal';
  }

  // Incident/resolved mode: app stays up, only a banner is shown
  if (config.mode === 'incident') {
    return 'incident';
  }

  if (config.mode === 'resolved') {
    return 'resolved';
  }

  const startTime = new Date(config.startTime).getTime();

  // If startTime is invalid, fail safe — don't block the app
  if (isNaN(startTime)) {
    return 'normal';
  }

  const now = Date.now();

  if (now >= startTime) {
    return 'active';
  }

  return 'scheduled';
};

/**
 * Format the maintenance start time for display.
 * Returns a locale-formatted string like "Apr 15, 2026, 2:00 AM"
 */
/**
 * Get a short timezone abbreviation (e.g., "IST", "EST", "PST").
 * Falls back to the browser's default short timezone name.
 */
const getTimezoneAbbreviation = (date: Date): string => {
  const tzName = Intl.DateTimeFormat().resolvedOptions().timeZone;
  // Format with long timezone name and extract the abbreviation from it
  const parts = new Intl.DateTimeFormat('en-US', {
    timeZoneName: 'long',
    timeZone: tzName,
  }).formatToParts(date);
  const longName = (parts.find((p) => p.type === 'timeZoneName') || {}).value || '';

  // Build abbreviation from capital letters in the long name
  // e.g., "India Standard Time" → "IST", "Eastern Standard Time" → "EST"
  const abbr = longName.replace(/[^A-Z]/g, '');
  if (abbr.length >= 2 && abbr.length <= 5) {
    return abbr;
  }

  // Fallback: use the browser's short format
  const shortParts = new Intl.DateTimeFormat('en-US', {
    timeZoneName: 'short',
    timeZone: tzName,
  }).formatToParts(date);
  return (shortParts.find((p) => p.type === 'timeZoneName') || {}).value || '';
};

export const formatMaintenanceTime = (isoString: string): string => {
  const date = new Date(isoString);
  if (isNaN(date.getTime())) {
    return '';
  }
  const dateStr = date.toLocaleString(undefined, {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
    hour: 'numeric',
    minute: '2-digit',
  });
  const tzAbbr = getTimezoneAbbreviation(date);
  return tzAbbr ? `${dateStr} ${tzAbbr}` : dateStr;
};

/**
 * SessionStorage key for banner dismissal — includes startTime
 * so a new maintenance window re-shows the banner.
 */
export const getBannerDismissalKey = (startTime: string): string => {
  return `maintenance-banner-dismissed-${startTime}`;
};

export const isBannerDismissed = (startTime: string): boolean => {
  return sessionStorage.getItem(getBannerDismissalKey(startTime)) === 'true';
};

export const dismissBanner = (startTime: string): void => {
  sessionStorage.setItem(getBannerDismissalKey(startTime), 'true');
};

/**
 * Clear banner dismissal on logout so the banner reappears
 * when a user logs back in on the same tab.
 */
export const clearBannerDismissal = (): void => {
  const config = getMaintenanceConfig();
  if (config.enabled && config.startTime) {
    sessionStorage.removeItem(getBannerDismissalKey(config.startTime));
  }
};
